import { useMemo, useState } from 'react'
import { useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useAssets } from '../data/fleet.ts'
import { labelBase, labelUrl } from '../labels/qr.ts'
import { BackLink, DisabledHint } from '../ui/components.tsx'
import { QrSvg } from '../ui/QrSvg.tsx'

/** A printable sheet of labels: a QR code that opens `/a/<TAG>`, and the tag in plain letters, so a damaged label still works
 *  (the scan page accepts the typed tag). Black on white whatever the theme: this is for paper. On paper (M23 item 39): an 8-mm page margin,
 *  a dashed border per label as the cut line, a tag that wraps instead of running past the border; the QR address under a fold. */
export function LabelsPage() {
  const { t } = useTranslation()
  const { ids } = useSearch({ from: '/labels' })
  const assets = useAssets()
  const active = useMemo(() => (assets ?? []).filter((a) => a.lifecycle !== 'retired'), [assets])
  const [picked, setPicked] = useState<Set<string>>(() => new Set((ids ?? '').split(',').filter(Boolean)))
  const [q, setQ] = useState('')
  const base = labelBase()
  const list = active.filter((a) => !q.trim() || [a.readable_tag, a.make, a.model].some((v) => (v ?? '').toLowerCase().includes(q.trim().toLowerCase())))
  const chosen = active.filter((a) => picked.has(a.id))
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })

  return (
    <section className="flex flex-col gap-4 print:gap-0">
      <div className="flex flex-col gap-4 print:hidden">
        <BackLink to="/assets">← {t('common.back')}</BackLink>
        <h1 className="text-2xl font-bold">{t('labels.title')}</h1>
        <p className="meta">{t('labels.help')}</p>
        <p className="meta">{t('labels.onlyActive')}</p>
        <input className="input" type="search" aria-label={t('common.search')} placeholder={t('assets.searchPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <button className="btn" onClick={() => setPicked(new Set(list.map((a) => a.id)))}>{t('labels.selectAll')}</button>
          <button className="btn" onClick={() => setPicked(new Set())}>{t('labels.selectNone')}</button>
        </div>
        {active.length === 0 && <p className="opacity-70">{t('labels.none')}</p>}
        <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto rounded-xl border-2 border-slate-300 p-2 dark:border-night-line" data-testid="label-picker">
          {list.map((a) => (
            <li key={a.id}>
              <label className="flex min-h-14 items-center gap-3 px-2 font-medium">
                <input type="checkbox" className="size-6" checked={picked.has(a.id)} onChange={() => toggle(a.id)} />
                <span className="font-bold">{a.readable_tag}</span><span className="meta truncate">{[a.make, a.model].filter(Boolean).join(' ')}</span>
              </label>
            </li>
          ))}
        </ul>
        <details className="group">
          <summary className="flex min-h-14 cursor-pointer list-none items-center gap-2 font-semibold [&::-webkit-details-marker]:hidden" data-testid="label-base-toggle">{t('labels.baseDetails')}</summary>
          <p className="meta break-all font-mono" data-testid="label-base">{t('labels.base', { url: base })}</p>
        </details>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-semibold" data-testid="label-count">{t('labels.selected', { count: chosen.length })}</p>
          <div className="flex flex-col gap-2">
            <DisabledHint show={chosen.length === 0} testId="print-hint">{t('labels.printHint')}</DisabledHint>
            <button className="btn btn-primary" disabled={chosen.length === 0} onClick={() => window.print()} data-testid="print-labels">{t('labels.print')}</button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 min-[560px]:grid-cols-2 print:grid-cols-3 print:gap-2 print:p-[8mm]" data-testid="label-sheet">
        {chosen.map((a) => (
          <div key={a.id} className="flex break-inside-avoid items-center gap-2 rounded-lg border-2 border-black bg-white p-2 text-black print:border-dashed print:border-slate-400" data-testid="label-card">
            <QrSvg text={labelUrl(a.readable_tag, base)} className="size-24 shrink-0 print:size-[24mm]" title={a.readable_tag} />
            <div className="min-w-0">
              <p className="break-all text-xl font-black leading-none" data-testid="label-tag">{a.readable_tag}</p>
              <p className="mt-1 break-words text-sm leading-tight">{[a.make, a.model].filter(Boolean).join(' ')}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
